import { api } from '@/api'
import to from '@/modules/await-to-js'
import getLinkHref from '@/modules/utils/api/getLinkHref'
import { camelCase, cloneDeep, map, filter, sortBy, each, isUndefined, isObject, find } from 'lodash-es'
import subHours from 'date-fns/sub_hours'
import addDays from 'date-fns/add_days'
import addMonths from 'date-fns/add_months'
import subYears from 'date-fns/sub_years'
import addYears from 'date-fns/add_years'
import getTime from 'date-fns/get_time'

export default {
  methods: {
    getKpi ({ customer, fleet, gateway, cluster, endDevice, kpi, period }) {
      // Check which entity has been selected
      let entity = null
      if (gateway) {
        entity = gateway
        this.entityId = gateway.eui
      } else if (endDevice) {
        entity = endDevice
        this.entityId = endDevice.devEui
      } else if (fleet && !gateway) {
        entity = fleet
        this.entityId = camelCase(fleet.name)
      } else if (cluster && !endDevice) {
        entity = cluster
        this.entityId = camelCase(cluster.name)
      } else if (customer && !fleet && !gateway && !cluster && !endDevice) {
        entity = customer
        this.entityId = camelCase(customer.name)
      }

      // If "all customers" is selected, link are in application
      if (entity.id === 0) entity = this.$store.getters.application

      // Get webservice url from entity and kpi
      const url = getLinkHref(entity, kpi.link)

      if (!url) return
      if (url.indexOf('kpis') !== -1) this.getRncKpi(url, arguments[0])
      else if (url.indexOf('statistics') !== -1) this.getBscKpi(url, arguments[0])
      else if (url.indexOf('connections') !== -1) this.getConnections(url, arguments[0])
    },

    async getRncKpi (url, { customer, fleet, gateway, cluster, endDevice, kpi, period }) {
      // Compile parameters for request
      const kpiParams = { period }
      if (kpi.groupBy) kpiParams[kpi.groupBy] = 'all'

      let error, response
      [error, response] = await to(api.getKpi(`${url}/${kpi.kpi}`, kpiParams))
      if (error || !response) return

      this.title = this.$t(kpi.name)
      this.kpi = cloneDeep(kpi)
      this.temporalUnit = period
      this.rawData = cloneDeep(response.data.list)
      this.dataProvider = map(response.data.list, data => {
        // Convert nanoseconds to miliseconds
        if (data.value && this.kpi.unit === 'ns') {
          data.value = Math.floor(data.value / 1000000)
        }
        // Convert timestamp as Number
        data.time = Number(data.time)
        return data
      })
      // Nanoseconds convertes, change unit
      if (this.kpi.unit === 'ns') this.kpi.unit = 'ms'
      // Convert data if chart type is bars
      if (this.kpi.chartType === 'bars') this.dataProvider = buildBarsDataProvider(this.dataProvider, this.kpi)
    },

    async getBscKpi (url, { customer, fleet, gateway, cluster, endDevice, kpi, period }) {
      // Compile parameters for request
      const kpiParams = {
        temporalUnit: period,
        startDate: getTime(subYears(new Date(), 1)),
        endDate: getTime(addYears(new Date(), 1))
      }
      if (kpi.link === 'gatewayNumericStatistics') kpiParams.metricNames = kpi.metricNames
      if (kpi.link === 'gatewayTermStatistics') kpiParams.metricName = kpi.metricNames

      // Get data and alarms
      const kpiPromise = api.getKpi(url, kpiParams)
      const alarmsPromise = kpi.alarms ? api.getKpiAlarms(gateway, kpi.alarms, kpiParams.startDate, kpiParams.endDate) : null

      const promises = []
      if (kpiPromise) promises.push(kpiPromise)
      if (alarmsPromise) promises.push(alarmsPromise)

      let error, responses, kpiResponse, alarmsResponse
      [error, responses] = await to(Promise.all(promises))
      if (error || !responses) return
      [kpiResponse, alarmsResponse] = responses

      this.title = this.$t(kpi.name)
      this.kpi = kpi
      this.temporalUnit = period
      this.rawData = cloneDeep(kpiResponse.data.list)
      this.dataProvider = map(kpiResponse.data.list, data => {
        // Flatten object
        data = { ...data, ...data.data }
        delete data.data

        // Calculate hysteresis value
        if (kpi.attrs.length > 2) {
          const threshold = find(kpi.attrs, attr => attr.toLowerCase().indexOf('threshold') !== -1)
          const hysteresis = find(kpi.attrs, attr => attr.toLowerCase().indexOf('hysteresis') !== -1)

          if (threshold && hysteresis) {
            if (kpi.metricNames === '@gsm') {
              // RSSI values are negatives
              data[hysteresis] = data[threshold] + data[hysteresis]
            } else {
              data[hysteresis] = data[threshold] - data[hysteresis]
            }

            // Find corresponding alarm if exists
            if (alarmsResponse.data.totalCount > 0) {
              const correspondingAlarms = filter(alarmsResponse.data.list, (alarm) => {
                // for hour temporal unit, alarms correspond to the previous period
                if (this.temporalUnit === 'HOUR') {
                  const timeStampSup = data.timestamp
                  const timeStampInf = getTime(subHours(timeStampSup, 1))
                  return timeStampInf < alarm.createTimeStamp && alarm.createTimeStamp <= timeStampSup
                }

                // for other temporal units, alarms correspond to the next period
                let timeStampSup = data.timestamp
                switch (this.temporalUnit) {
                  case 'DAY':
                    timeStampSup = getTime(addDays(data.timestamp, 1))
                    break
                  case 'MONTH':
                    timeStampSup = getTime(addMonths(data.timestamp, 1))
                    break
                  case 'YEAR':
                    timeStampSup = getTime(addYears(data.timestamp, 1))
                    break
                }
                const timeStampInf = data.timestamp
                return timeStampInf <= alarm.createTimeStamp && alarm.createTimeStamp < timeStampSup
              })

              if (correspondingAlarms.length > 0) {
                data.alarmThreshold = data[threshold]
                data.alarmBulletSize = Math.min(5 + correspondingAlarms.length, 15)
                data.alarms = correspondingAlarms
              }
            }
          }
        }

        return data
      })
      // Convert data if chart type is bars
      if (this.kpi.chartType === 'bars') this.dataProvider = buildBarsDataProvider(this.dataProvider, this.kpi)
    },

    async getConnections (url, { customer, fleet, gateway, cluster, endDevice, kpi, period }) {
      let error, response
      [error, response] = await to(api.getKpi(url))
      if (error || !response) return

      this.title = this.$t(kpi.name)
      this.kpi = kpi
      this.temporalUnit = 'HOUR'
      this.rawData = cloneDeep(response.data.list)
      this.dataProvider = buildConnectionsDataProvider(response.data.list)
    }
  }
}

const buildConnectionsDataProvider = (responseDataList) => {
  let intervals = cloneDeep(responseDataList)
  if (!intervals || intervals.length === 0) return []

  // Data parsing constants
  const dataProvider = []
  // 10 minutes in milliseconds
  const period = 10 * 60 * 1000
  const getNextDate = function (date) {
    return Math.floor((date + period) / period) * period
  }

  // Chronological sorting
  intervals = sortBy(intervals, ['startDate', 'endDate'])

  // Manage end date coherence
  for (let i = 0; i < intervals.length; i++) {
    if (intervals[i].endDate) {
      if (intervals[i + 1] && intervals[i + 1].startDate) {
        intervals[i].endDate = Math.min(intervals[i].endDate, intervals[i + 1].startDate)
      }
    } else {
      if (intervals[i + 1] && intervals[i + 1].startDate) {
        intervals[i].endDate = intervals[i + 1].startDate
      } else {
        intervals[i].endDate = getTime(new Date())
      }
    }
  }

  // Build dataProvider
  each(intervals, (interval) => {
    // First data of the interval
    dataProvider.push({
      time: interval.startDate,
      connected: interval.connected ? 1 : 0,
      disconnected: isUndefined(interval.connected) || interval.connected ? 0 : 1,
      networkBearer: interval.networkBearer
    })

    // Create data every 10 minutes of the interval
    let nextDate = getNextDate(interval.startDate)
    while (nextDate < interval.endDate) {
      dataProvider.push({
        time: nextDate,
        connected: interval.connected ? 1 : 0,
        disconnected: isUndefined(interval.connected) || interval.connected ? 0 : 1,
        networkBearer: interval.networkBearer
      })
      nextDate = getNextDate(nextDate)
    }

    // Last data of the interval
    dataProvider.push({
      time: interval.endDate,
      connected: interval.connected ? 1 : 0,
      disconnected: isUndefined(interval.connected) || interval.connected ? 0 : 1,
      networkBearer: interval.networkBearer
    })
  })

  return dataProvider
}

const buildBarsDataProvider = (data, kpi) => {
  // Already in correct format
  if (data.length && isObject(data[0][kpi.attrs[0]])) return data

  // Convert from raw value to object data
  const formattedData = []
  for (let i = 0; i < data.length; i++) {
    const elem = data[i]

    for (let attr of kpi.attrs) {
      const value = elem[attr]
      elem[attr] = {}
      elem[attr][`${attr}Data`] = value
    }

    formattedData.push(elem)
  }

  return data
}
